import { Alert, Snackbar } from '@mui/material'
import { type ReactNode, useCallback, useMemo, useState } from 'react'

import {
  SnackbarContext,
  type SnackbarContextValue,
  type SnackbarSeverity,
} from './snackbarContext'

interface SnackbarState {
  open: boolean
  message: string
  severity: SnackbarSeverity
}

const AUTO_HIDE_DURATION_MS = 5000

const initialState: SnackbarState = { open: false, message: '', severity: 'success' }

interface SnackbarProviderProps {
  children: ReactNode
}

/**
 * Owns the app's single toast outlet.
 *
 * Mounted above the router so a toast raised from inside a modal (or any other
 * component that unmounts on success) stays on screen after its caller is gone.
 */
export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [snackbar, setSnackbar] = useState<SnackbarState>(initialState)

  const show = useCallback((message: string, severity: SnackbarSeverity) => {
    setSnackbar({ open: true, message, severity })
  }, [])

  const handleClose = () => setSnackbar((current) => ({ ...current, open: false }))

  const value = useMemo<SnackbarContextValue>(
    () => ({
      showSuccess: (message) => show(message, 'success'),
      showError: (message) => show(message, 'error'),
      showWarning: (message) => show(message, 'warning'),
      showInfo: (message) => show(message, 'info'),
    }),
    [show]
  )

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={AUTO_HIDE_DURATION_MS}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleClose} variant='filled'>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  )
}
